import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '../useApiQuery';
import { apiGet, apiPatch, apiPut, operationsClient } from '@/lib/api';
import type { LocationContactNotificationType, ResolvedRecipient } from './useLocationContacts';

/**
 * Who this JOB would actually reach for one kind of message.
 *
 * The job's own answer, not the location's. A bell can be switched off for
 * just this job, and the location endpoint has no job context — so a preview
 * built from it could promise an SMS the switch beside it had already turned
 * off. Keyed on the job so setting a bell refreshes it.
 */
export function useJobRecipients(jobId: string | undefined, type: LocationContactNotificationType) {
  return useApiQuery<ResolvedRecipient[]>({
    queryKey: keys.recipients(jobId ?? 'none', type),
    queryFn: () =>
      apiGet<ResolvedRecipient[]>(
        operationsClient,
        `/jobs/${jobId}/contact-notifications/recipients?type=${type}`,
      ),
    enabled: !!jobId,
  });
}

/** Which scope a switch was set in — the job screen shows all three. */
export type NotificationScope = 'job' | 'site' | 'contact';

export type NotificationChannel = 'SMS' | 'EMAIL';

export interface JobContactChannel {
  channel: NotificationChannel;
  enabled: boolean;
  /** Which scope switched it off, or null when it is on. */
  blockedBy: 'no-channel' | 'contact' | 'site' | 'job' | null;
  /** True when THIS job holds an explicit choice for the channel. */
  overriddenHere: boolean;
}

export interface JobContactNotification {
  contactId: string;
  displayName: string;
  /** The widest role they hold here. */
  role: string;
  /** Every role they hold at this site — one person, several badges. */
  roles?: string[];
  title: string | null;
  phone: string | null;
  email: string | null;
  channels: JobContactChannel[];
}

const keys = {
  list: (jobId: string) => ['jobs', jobId, 'contact-notifications'] as const,
  recipients: (jobId: string, type: string) => [...keys.list(jobId), 'recipients', type] as const,
};

export function useJobContactNotifications(jobId: string | undefined) {
  return useApiQuery<JobContactNotification[]>({
    queryKey: keys.list(jobId ?? 'none'),
    queryFn: () =>
      apiGet<JobContactNotification[]>(operationsClient, `/jobs/${jobId}/contact-notifications`),
    enabled: !!jobId,
  });
}

/**
 * Set a channel for one job only. `null` clears the override and hands the
 * decision back to the site and the contact record.
 */
export function useSetJobContactChannel(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      contactId,
      ...body
    }: {
      contactId: string;
      smsEnabled?: boolean | null;
      emailEnabled?: boolean | null;
    }) => apiPut(operationsClient, `/jobs/${jobId}/contact-notifications/${contactId}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list(jobId) });
    },
  });
}

/**
 * The widest scope: this person, everywhere in the system. These are the same
 * flags the communication gate reads before any automated send, which is why
 * switching one off here cannot be undone from a site or a job.
 */
export function useSetContactChannels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      contactId,
      ...body
    }: {
      contactId: string;
      smsFollowUp?: boolean;
      emailFollowUp?: boolean;
    }) => apiPatch(operationsClient, `/contacts/${contactId}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      void qc.invalidateQueries({ queryKey: ['locations'] });
      void qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export const SCOPE_LABEL: Record<NotificationScope, string> = {
  job: 'Just this job',
  site: 'This location',
  contact: 'Everywhere — every job and location',
};

export const BLOCKED_REASON: Record<'no-channel' | 'contact' | 'site' | 'job', string> = {
  'no-channel': 'No number or address on file',
  contact: 'Switched off for this contact everywhere',
  site: 'Switched off for this location',
  job: 'Switched off for this job',
};
